'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { STALE_TIMES, CACHE_TIMES } from '@/providers/QueryProvider'

// =====================================
// Query Keys
// =====================================
export const clinicKeys = {
  all: ['clinic'] as const,
  
  // Staff list
  staff: (clinicId: string) => 
    [...clinicKeys.all, 'staff', clinicId] as const,
  
  // Dashboard data
  dashboard: (clinicId: string) => 
    [...clinicKeys.all, 'dashboard', clinicId] as const,
  dashboardWithStaff: (clinicId: string, staffId: string) => 
    [...clinicKeys.dashboard(clinicId), staffId] as const,
  
  // Patients list (for search)
  patients: (clinicId: string) => 
    [...clinicKeys.all, 'patients', clinicId] as const,
  patientsSearch: (clinicId: string, search: string) => 
    [...clinicKeys.patients(clinicId), search] as const,
  
  // Notifications
  notifications: (clinicId: string, userId: string) => 
    [...clinicKeys.all, 'notifications', clinicId, userId] as const,
  
  // Absences
  absences: (clinicId: string) => 
    [...clinicKeys.all, 'absences', clinicId] as const,
  absencesWithRange: (clinicId: string, startDate?: string, endDate?: string) => 
    [...clinicKeys.absences(clinicId), startDate, endDate] as const,
    
  // Waiting list
  waitingList: (clinicId: string) => 
    [...clinicKeys.all, 'waiting-list', clinicId] as const,
}

// =====================================
// Types
// =====================================
interface StaffMember {
  id: string
  full_name: string
  email: string
  role: string
  is_active: boolean
}

interface Absence {
  id: string
  user_id: string
  start_date: string
  end_date: string
  absence_type: string
  status: string
  notes?: string
  user?: {
    full_name: string
  }
}

// =====================================
// Fetch Functions
// =====================================
async function fetchStaff(clinicId: string): Promise<StaffMember[]> {
  const response = await fetch(`/api/clinic/staff?clinicId=${clinicId}`)
  if (!response.ok) throw new Error('Failed to fetch staff')
  const data = await response.json()
  return data.staff || data || []
}

async function fetchAbsences(
  clinicId: string,
  startDate?: string,
  endDate?: string
): Promise<{ absences: Absence[] }> {
  const params = new URLSearchParams({ clinicId })
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  
  const response = await fetch(`/api/hr/absences?${params}`)
  if (!response.ok) throw new Error('Failed to fetch absences')
  return response.json()
}

async function fetchPatients(clinicId: string, search?: string) {
  const params = new URLSearchParams({ clinic_id: clinicId })
  if (search) params.append('search', search)
  
  const response = await fetch(`/api/patients?${params}`)
  if (!response.ok) throw new Error('Failed to fetch patients')
  return response.json()
}

async function fetchDashboard(clinicId: string, staffId?: string) {
  let url = `/api/clinic/dashboard?clinicId=${clinicId}`
  if (staffId) {
    url = `/api/staff/dashboard-with-financials?staffId=${staffId}&clinicId=${clinicId}`
  }
  
  const response = await fetch(url)
  if (!response.ok) throw new Error('Failed to fetch dashboard')
  return response.json()
}

async function fetchNotifications(clinicId: string, userId: string) {
  const response = await fetch(`/api/clinic/notifications?clinicId=${clinicId}&userId=${userId}`)
  if (!response.ok) throw new Error('Failed to fetch notifications')
  return response.json()
}

async function fetchWaitingList(clinicId: string) {
  const response = await fetch(`/api/waiting-list/clinic?clinicId=${clinicId}`)
  if (!response.ok) throw new Error('Failed to fetch waiting list')
  return response.json()
}

// =====================================
// Query Hooks
// =====================================

/**
 * Fetch clinic staff with 30-minute cache
 */
export function useClinicStaff(clinicId: string, enabled = true) {
  return useQuery({
    queryKey: clinicKeys.staff(clinicId),
    queryFn: () => fetchStaff(clinicId),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.LONG,
  })
}

/**
 * Fetch absences with 30-minute cache
 */
export function useAbsences(
  clinicId: string,
  startDate?: string,
  endDate?: string,
  enabled = true
) {
  return useQuery({
    queryKey: clinicKeys.absencesWithRange(clinicId, startDate, endDate),
    queryFn: () => fetchAbsences(clinicId, startDate, endDate),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.HR_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch patients with caching
 */
export function usePatients(clinicId: string, search?: string, enabled = true) {
  return useQuery({
    queryKey: clinicKeys.patientsSearch(clinicId, search || ''),
    queryFn: () => fetchPatients(clinicId, search),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.PATIENT_DATA,
    gcTime: CACHE_TIMES.LONG,
  })
}

/**
 * Fetch dashboard data with caching
 */
export function useDashboard(clinicId: string, staffId?: string, enabled = true) {
  return useQuery({
    queryKey: staffId 
      ? clinicKeys.dashboardWithStaff(clinicId, staffId)
      : clinicKeys.dashboard(clinicId),
    queryFn: () => fetchDashboard(clinicId, staffId),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch notifications with shorter cache (5 min for real-time feel)
 */
export function useNotifications(clinicId: string, userId: string, enabled = true) {
  return useQuery({
    queryKey: clinicKeys.notifications(clinicId, userId),
    queryFn: () => fetchNotifications(clinicId, userId),
    enabled: enabled && !!clinicId && !!userId,
    staleTime: STALE_TIMES.REALTIME,
    gcTime: CACHE_TIMES.SHORT,
  })
}

/**
 * Fetch waiting list with caching
 */
export function useWaitingList(clinicId: string, enabled = true) {
  return useQuery({
    queryKey: clinicKeys.waitingList(clinicId),
    queryFn: () => fetchWaitingList(clinicId),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.CLINIC_DATA,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Mark notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: { notificationId: string; clinicId: string; userId: string }) => {
      const response = await fetch('/api/clinic/notifications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: data.notificationId, read: true })
      })
      if (!response.ok) throw new Error('Failed to mark as read')
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: clinicKeys.notifications(variables.clinicId, variables.userId)
      })
    }
  })
}

/**
 * Prefetch staff for faster navigation
 */
export function usePrefetchStaff() {
  const queryClient = useQueryClient()
  
  return (clinicId: string) => {
    queryClient.prefetchQuery({
      queryKey: clinicKeys.staff(clinicId),
      queryFn: () => fetchStaff(clinicId),
      staleTime: STALE_TIMES.CLINIC_DATA,
    })
  }
}
